import React, { useState, useRef, useEffect, useCallback } from 'react';
import { searchVendors, searchCategories } from '../../services/shipturtleApi';
import './ManageProducts.css';

function ProductFilters({ 
  filters, 
  onFilterChange, 
  isReadOnly, 
  userRole, 
  categories = [], 
  vendors = [], 
  hideStatusFilter = false,
  hideProductStatusFilter = false,
  hideStatusFromActive = false,
  inventoryFullRow = false,
  dateRangeAtStatusPosition = false,
  // Off by default: only Manage Products maps these onto API params, so showing
  // them elsewhere would render filters that silently do nothing.
  showStoreLinkFilters = false
}) {
  const [localFilters, setLocalFilters] = useState(filters);
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  
  // Vendor autocomplete state
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const vendorDropdownRef = useRef(null);
  const vendorSearchTimeoutRef = useRef(null);
  
  // Category autocomplete state
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const categoryDropdownRef = useRef(null);
  const categorySearchTimeoutRef = useRef(null);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target)) {
        setShowVendorDropdown(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Initialize vendor search term from selected vendor
  useEffect(() => {
    if (localFilters.vendor && localFilters.vendor !== 'all') {
      const vendor = vendors.find(v => v.id === localFilters.vendor);
      if (vendor) {
        setSelectedVendor(vendor);
        setVendorSearchTerm(vendor.brand_name || vendor.company_name);
      }
    } else {
      setSelectedVendor(null);
      setVendorSearchTerm('');
    }
  }, [localFilters.vendor, vendors]);
  
  // Initialize category search term from selected category
  useEffect(() => {
    if (localFilters.category && localFilters.category !== 'all') {
      const category = categories.find(c => c.name === localFilters.category || c.id === localFilters.category);
      if (category) {
        setSelectedCategory(category);
        setCategorySearchTerm(category.name || localFilters.category);
      } else {
        // Category not in initial list, just show the text
        setCategorySearchTerm(localFilters.category);
      }
    } else {
      setSelectedCategory(null);
      setCategorySearchTerm('');
    }
  }, [localFilters.category, categories]);
  
  // Server-side vendor search with debouncing and smart sorting
  const performVendorSearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setVendorSuggestions([]);
      setVendorLoading(false);
      return;
    }
    
    try {
      setVendorLoading(true);
      const response = await searchVendors(searchTerm, 20);
      const results = response.vendors || response.results || [];
      
      const searchLower = searchTerm.toLowerCase();
      
      // Filter: Only show vendors where the DISPLAYED name contains the search term
      const filteredResults = results.filter(vendor => {
        const displayName = (vendor.brand_name || vendor.company_name || '').toLowerCase();
        return displayName.includes(searchLower);
      });
      
      // Sort results: prioritize those starting with search term
      const sortedResults = filteredResults.sort((a, b) => {
        const aName = (a.brand_name || a.company_name || '').toLowerCase();
        const bName = (b.brand_name || b.company_name || '').toLowerCase();
        
        const aStarts = aName.startsWith(searchLower);
        const bStarts = bName.startsWith(searchLower);
        
        // Prioritize matches that start with search term
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        
        // Alphabetical for same priority
        return aName.localeCompare(bName);
      });
      
      setVendorSuggestions(sortedResults);
    } catch (error) {
      console.error('Error searching vendors:', error);
      setVendorSuggestions([]);
    } finally {
      setVendorLoading(false);
    }
  }, []);
  
  // Server-side category search with debouncing and smart sorting
  const performCategorySearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setCategorySuggestions([]);
      setCategoryLoading(false);
      return;
    }
    
    try {
      setCategoryLoading(true);
      const response = await searchCategories(searchTerm, 20);
      const results = response.categories || [];
      
      const searchLower = searchTerm.toLowerCase();
      
      // Filter: Only show categories where the name contains the search term
      const filteredResults = results.filter(category => {
        const categoryName = (category.name || '').toLowerCase();
        return categoryName.includes(searchLower);
      });
      
      // Sort results: prioritize those starting with search term
      const sortedResults = filteredResults.sort((a, b) => {
        const aName = (a.name || '').toLowerCase();
        const bName = (b.name || '').toLowerCase();
        
        const aStarts = aName.startsWith(searchLower);
        const bStarts = bName.startsWith(searchLower);
        
        // Prioritize matches that start with search term
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        
        // Alphabetical for same priority
        return aName.localeCompare(bName);
      });
      
      setCategorySuggestions(sortedResults);
    } catch (error) {
      console.error('Error searching categories:', error);
      setCategorySuggestions([]);
    } finally {
      setCategoryLoading(false);
    }
  }, []);
  
  // Helper function to highlight matching text
  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    const term = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Split keeps the captured match, so odd indices are always the matches.
    // Don't use regex.test() to decide: the /g flag makes it stateful via
    // lastIndex and it would flip between true/false across calls.
    const parts = text.split(new RegExp(`(${term})`, 'gi'));

    // Wrapped in a single element so the fragments stay one unit even if
    // .dropdownItem ever resolves to display:flex; a bare array would make
    // every fragment its own flex item, spreading the name across the row.
    return (
      <span className="highlightText">
        {parts.map((part, index) =>
          part === '' ? null : index % 2 === 1
            ? <mark key={index}>{part}</mark>
            : <React.Fragment key={index}>{part}</React.Fragment>
        )}
      </span>
    );
  };
  
  const handleInputChange = (field, value) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
    
    // Show custom date range picker when "Custom Range" is selected
    if (field === 'dateRange') {
      setShowCustomDateRange(value === 'custom');
    }
  };
  
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalFilters(prev => ({ ...prev, globalSearch: value }));
    // Debounce search to avoid too many API calls
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      onFilterChange({ ...localFilters, globalSearch: value });
    }, 300);
  };
  
  // Handle vendor search input with debouncing
  const handleVendorSearchChange = (e) => {
    const value = e.target.value;
    setVendorSearchTerm(value);
    setShowVendorDropdown(true);
    
    // Clear previous timeout
    if (vendorSearchTimeoutRef.current) {
      clearTimeout(vendorSearchTimeoutRef.current);
    }
    
    // If user cleared the input, reset the filter
    if (!value || value.trim().length === 0) {
      setSelectedVendor(null);
      setVendorSuggestions([]);
      handleInputChange('vendor', 'all');
      return;
    }
    
    // Debounce API call
    setVendorLoading(true);
    vendorSearchTimeoutRef.current = setTimeout(() => {
      performVendorSearch(value);
    }, 300);
  };
  
  // Handle category search input with debouncing
  const handleCategorySearchChange = (e) => {
    const value = e.target.value;
    setCategorySearchTerm(value);
    setShowCategoryDropdown(true);
    
    // Clear previous timeout
    if (categorySearchTimeoutRef.current) {
      clearTimeout(categorySearchTimeoutRef.current);
    }
    
    // If user cleared the input, reset the filter
    if (!value || value.trim().length === 0) {
      setSelectedCategory(null);
      setCategorySuggestions([]);
      handleInputChange('category', 'all');
      return;
    }
    
    // Debounce API call
    setCategoryLoading(true);
    categorySearchTimeoutRef.current = setTimeout(() => {
      performCategorySearch(value);
    }, 300);
  };
  
  const handleVendorSelect = (vendor) => {
    if (!vendor) {
      // "All Vendors" selected
      setSelectedVendor(null);
      setVendorSearchTerm('');
      setVendorSuggestions([]);
      setShowVendorDropdown(false);
      handleInputChange('vendor', 'all');
    } else {
      setSelectedVendor(vendor);
      setVendorSearchTerm(vendor.brand_name || vendor.company_name);
      setShowVendorDropdown(false);
      setVendorSuggestions([]);
      handleInputChange('vendor', vendor.id);
    }
  };
  
  const handleCategorySelect = (category) => {
    if (!category) {
      // "All Categories" selected
      setSelectedCategory(null);
      setCategorySearchTerm('');
      setCategorySuggestions([]);
      setShowCategoryDropdown(false);
      handleInputChange('category', 'all');
    } else {
      setSelectedCategory(category);
      setCategorySearchTerm(category.name);
      setShowCategoryDropdown(false);
      setCategorySuggestions([]);
      handleInputChange('category', category.name);
    }
  };
  
  const handleInventoryChange = (field, value) => {
    const newFilters = { ...localFilters, inventory: { ...localFilters.inventory, [field]: value } };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };
  
  const clearFilters = () => {
    const clearedFilters = {
      globalSearch: '',
      status: 'all',
      category: 'all',
      vendor: 'all',
      channel: 'all',
      inventory: { min: '', max: '' },
      dateRange: 'all',
      productStatus: 'all',
      linkingStatus: 'all',
      mappingStatus: 'all'
    };
    setLocalFilters(clearedFilters);
    setVendorSearchTerm('');
    setCategorySearchTerm('');
    setSelectedVendor(null);
    setSelectedCategory(null);
    onFilterChange(clearedFilters);
  };
  
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (vendorSearchTimeoutRef.current) {
        clearTimeout(vendorSearchTimeoutRef.current);
      }
      if (categorySearchTimeoutRef.current) {
        clearTimeout(categorySearchTimeoutRef.current);
      }
      if (window.searchTimeout) {
        clearTimeout(window.searchTimeout);
      }
    };
  }, []);
  
  // Optionally ignore the status field when determining whether any filters are active.
  const hasActiveFilters = localFilters.globalSearch || 
                          (hideStatusFromActive ? false : localFilters.status !== 'all') || 
                          localFilters.category !== 'all' ||
                          localFilters.vendor !== 'all' ||
                          localFilters.channel !== 'all' ||
                          localFilters.inventory?.min ||
                          localFilters.inventory?.max ||
                          localFilters.dateRange !== 'all' ||
                          localFilters.productStatus !== 'all' ||
                          (localFilters.linkingStatus || 'all') !== 'all' ||
                          (localFilters.mappingStatus || 'all') !== 'all';

  return (
    <div className="filtersContainer">
      <div className="filtersHeader">
        <h3>Filters</h3>
        {hasActiveFilters && (
          <button 
            onClick={clearFilters}
            className="clearButton"
            disabled={isReadOnly}
          >
            Clear All
          </button>
        )}
      </div>
      
      <div className="filtersGrid">
        {/* Global Search Input */}
        <div className="filterGroup filterGroupWide">
          <label htmlFor="globalSearch" className="filterLabel">
            Global Search
          </label>
          <input
            id="globalSearch"
            type="text"
            placeholder="Search by title, SKU, vendor, or tags..."
            value={localFilters.globalSearch || ''}
            onChange={handleSearchChange}
            className="searchInput"
            disabled={isReadOnly}
          />
        </div>
        
        {/* Status Filter (or Date Range in the same position for Unlisted view) */}
        {dateRangeAtStatusPosition ? (
          <div className="filterGroup">
            <label htmlFor="dateRange_statusPos" className="filterLabel">
              Date Range
            </label>
            <select
              id="dateRange_statusPos"
              value={localFilters.dateRange || 'all'}
              onChange={(e) => handleInputChange('dateRange', e.target.value)}
              className="selectInput"
              disabled={isReadOnly}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
            {showCustomDateRange && (
              <div className="customDateRange">
                <div className="dateInputGroup">
                  <label htmlFor="startDate_statusPos">From:</label>
                  <input
                    type="date"
                    id="startDate_statusPos"
                    className="dateInput"
                    disabled={isReadOnly}
                  />
                </div>
                <div className="dateInputGroup">
                  <label htmlFor="endDate_statusPos">To:</label>
                  <input
                    type="date"
                    id="endDate_statusPos"
                    className="dateInput"
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          !hideStatusFilter && (
            <div className="filterGroup">
              <label htmlFor="status" className="filterLabel">
                Status
              </label>
              <select
                id="status"
                value={localFilters.status || 'all'}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="selectInput"
                disabled={isReadOnly}
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="active">Active / Approved</option>
                <option value="archived">Archived</option>
                <option value="deleted">Deleted</option>
              </select>
            </div>
          )
        )}
        
        {/* Category Filter - Server-side autocomplete */}
        <div className="filterGroup">
          <label htmlFor="category" className="filterLabel">
            Category
          </label>
          <div className="searchableDropdown" ref={categoryDropdownRef}>
            <input
              id="category"
              type="text"
              placeholder="Search categories..."
              value={categorySearchTerm}
              onChange={handleCategorySearchChange}
              onFocus={() => {
                setShowCategoryDropdown(true);
                if (categorySearchTerm.trim().length >= 2) {
                  performCategorySearch(categorySearchTerm);
                }
              }}
              className="searchInput searchInputNarrow"
              disabled={isReadOnly}
              autoComplete="off"
            />
            {showCategoryDropdown && !isReadOnly && (
              <div className="dropdownMenu">
                <div 
                  className="dropdownItem"
                  onClick={() => handleCategorySelect(null)}
                >
                  All Categories
                </div>
                {categoryLoading ? (
                  <div className="dropdownItem dropdownItemDisabled">
                    Searching...
                  </div>
                ) : categorySuggestions.length > 0 ? (
                  categorySuggestions.map((category) => (
                    <div
                      key={category.id || category.name}
                      className="dropdownItem"
                      onClick={() => handleCategorySelect(category)}
                    >
                      {highlightMatch(category.name, categorySearchTerm)}
                    </div>
                  ))
                ) : categorySearchTerm.trim().length >= 2 ? (
                  <div className="dropdownItem dropdownItemDisabled">
                    No categories found
                  </div>
                ) : (
                  <div className="dropdownItem dropdownItemDisabled">
                    Type at least 2 characters...
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Vendor Filter - Server-side autocomplete - Only show for admin role */}
        {userRole === 'admin' && (
          <div className="filterGroup">
            <label htmlFor="vendor" className="filterLabel">
              Vendor
            </label>
            <div className="searchableDropdown" ref={vendorDropdownRef}>
              <input
                id="vendor"
                type="text" 
                placeholder="Search vendors..."
                value={vendorSearchTerm}
                onChange={handleVendorSearchChange}
                onFocus={() => {
                  setShowVendorDropdown(true);
                  if (vendorSearchTerm.trim().length >= 2) {
                    performVendorSearch(vendorSearchTerm);
                  }
                }}
                className="searchInput searchInputNarrow"
                disabled={isReadOnly}
                autoComplete="off"
              />
              {showVendorDropdown && !isReadOnly && (
                <div className="dropdownMenu">
                  <div 
                    className="dropdownItem"
                    onClick={() => handleVendorSelect(null)}
                  >
                    All Vendors
                  </div>
                  {vendorLoading ? (
                    <div className="dropdownItem dropdownItemDisabled">
                      Searching...
                    </div>
                  ) : vendorSuggestions.length > 0 ? (
                    vendorSuggestions.map((vendor) => (
                      <div
                        key={vendor.id}
                        className="dropdownItem"
                        onClick={() => handleVendorSelect(vendor)}
                      >
                        {highlightMatch(vendor.brand_name || vendor.company_name, vendorSearchTerm)}
                      </div>
                    ))
                  ) : vendorSearchTerm.trim().length >= 2 ? (
                    <div className="dropdownItem dropdownItemDisabled">
                      No vendors found
                    </div>
                  ) : (
                    <div className="dropdownItem dropdownItemDisabled">
                      Type at least 2 characters...
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Channel Filter */}
        <div className="filterGroup">
          <label htmlFor="channel" className="filterLabel">
            Channel
          </label>
          <select
            id="channel"
            value={localFilters.channel || 'all'}
            onChange={(e) => handleInputChange('channel', e.target.value)}
            className="selectInput"
            disabled={isReadOnly}
          >
            <option value="all">All Channels</option>
            <option value="amazon">Amazon</option>
            <option value="shopify">Shopify</option>
            <option value="ebay">eBay</option>
            <option value="walmart">Walmart</option>
            <option value="etsy">Etsy</option>
          </select>
        </div>
        
        {/* Product Status Filter */}
        {!hideProductStatusFilter && (
          <div className="filterGroup">
            <label htmlFor="productStatus" className="filterLabel">
              Product Status
            </label>
            <select
              id="productStatus"
              value={localFilters.productStatus || 'all'}
              onChange={(e) => handleInputChange('productStatus', e.target.value)}
              className="selectInput"
              disabled={isReadOnly}
            >
              <option value="all">All Product Statuses</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
              <option value="unlisted">Unlisted</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
        )}

        {/* Linking Status Filter — does the product have store ids on file?
            Survives unmapping, so it's a separate axis from Mapping Status. */}
        {showStoreLinkFilters && (
          <div className="filterGroup">
            <label htmlFor="linkingStatus" className="filterLabel">
              Linking Status
            </label>
            <select
              id="linkingStatus"
              value={localFilters.linkingStatus || 'all'}
              onChange={(e) => handleInputChange('linkingStatus', e.target.value)}
              className="selectInput"
              disabled={isReadOnly}
            >
              <option value="all">All Linking Statuses</option>
              <option value="linked">Linked</option>
              <option value="unlinked">Not Linked</option>
            </select>
          </div>
        )}

        {/* Mapping Status Filter — is vendor sync switched on? A linked product
            can be unmapped, which is exactly the case worth finding. */}
        {showStoreLinkFilters && (
          <div className="filterGroup">
            <label htmlFor="mappingStatus" className="filterLabel">
              Mapping Status
            </label>
            <select
              id="mappingStatus"
              value={localFilters.mappingStatus || 'all'}
              onChange={(e) => handleInputChange('mappingStatus', e.target.value)}
              className="selectInput"
              disabled={isReadOnly}
            >
              {/* Both options are scoped to linked products — mapping is a
                  property of a link. Never-linked products live under
                  Linking Status, not here. */}
              <option value="all">All Mapping Statuses</option>
              <option value="mapped">Mapped (linked &amp; syncing)</option>
              <option value="unmapped">Unmapped (linked, not syncing)</option>
            </select>
          </div>
        )}

        {/* Inventory Level Filter */}
        <div className={"filterGroup" + (inventoryFullRow ? ' inventoryFullRow' : '')}>
          <label className="filterLabel">Inventory Level</label>
          <div className="inventoryInputs">
            <input
              type="number"
              placeholder="Min"
              value={localFilters.inventory?.min || ''}
              onChange={(e) => handleInventoryChange('min', e.target.value)}
              className="inventoryInput"
              disabled={isReadOnly}
              min="0"
            />
            <span className="inventorySeparator">-</span>
            <input
              type="number"
              placeholder="Max"
              value={localFilters.inventory?.max || ''}
              onChange={(e) => handleInventoryChange('max', e.target.value)}
              className="inventoryInput"
              disabled={isReadOnly}
              min="0"
            />
          </div>
        </div>
        
        {/* Date Range Filter (render here only if not shown at the status position) */}
        {!dateRangeAtStatusPosition && (
          <div className="filterGroup filterGroupDateRange">
            <label htmlFor="dateRange" className="filterLabel">
              Date Range
            </label>
            <select
              id="dateRange"
              value={localFilters.dateRange || 'all'}
              onChange={(e) => handleInputChange('dateRange', e.target.value)}
              className="selectInput"
              disabled={isReadOnly}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
            {showCustomDateRange && (
              <div className="customDateRange">
                <div className="dateInputGroup">
                  <label htmlFor="startDate">From:</label>
                  <input
                    type="date"
                    id="startDate"
                    className="dateInput"
                    disabled={isReadOnly}
                  />
                </div>
                <div className="dateInputGroup">
                  <label htmlFor="endDate">To:</label>
                  <input
                    type="date"
                    id="endDate"
                    className="dateInput"
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="activeFilters">
          <span className="activeFiltersLabel">Active Filters:</span>
          <div className="filterTags">
            {localFilters.globalSearch && (
              <span className="filterTag">
                Search: "{localFilters.globalSearch}"
                <button 
                  onClick={() => handleInputChange('globalSearch', '')}
                  className="removeTag"
                  disabled={isReadOnly}
                >
                  ×
                </button>
              </span>
            )}
            {localFilters.status !== 'all' && (
              <span className="filterTag">
                Status: {localFilters.status.replace('_', ' ')}
                <button 
                  onClick={() => handleInputChange('status', 'all')}
                  className="removeTag"
                  disabled={isReadOnly}
                >
                  ×
                </button>
              </span>
            )}
            {localFilters.category !== 'all' && localFilters.category && (
              <span className="filterTag">
                Category: {selectedCategory ? selectedCategory.name : localFilters.category}
                <button 
                  onClick={() => handleCategorySelect(null)}
                  className="removeTag"
                  disabled={isReadOnly}
                >
                  ×
                </button>
              </span>
            )}
            {localFilters.vendor !== 'all' && localFilters.vendor && (
              <span className="filterTag">
                Vendor: {selectedVendor ? (selectedVendor.brand_name || selectedVendor.company_name) : localFilters.vendor}
                <button 
                  onClick={() => handleVendorSelect(null)}
                  className="removeTag"
                  disabled={isReadOnly}
                >
                  ×
                </button>
              </span>
            )}
            {localFilters.channel !== 'all' && (
              <span className="filterTag">
                Channel: {localFilters.channel}
                <button 
                  onClick={() => handleInputChange('channel', 'all')}
                  className="removeTag"
                  disabled={isReadOnly}
                >
                  ×
                </button>
              </span>
            )}
            {localFilters.productStatus !== 'all' && (
              <span className="filterTag">
                Product Status: {localFilters.productStatus}
                <button
                  onClick={() => handleInputChange('productStatus', 'all')}
                  className="removeTag"
                  disabled={isReadOnly}
                >
                  ×
                </button>
              </span>
            )}
            {(localFilters.linkingStatus || 'all') !== 'all' && (
              <span className="filterTag">
                Linking: {localFilters.linkingStatus === 'linked' ? 'Linked' : 'Not Linked'}
                <button
                  onClick={() => handleInputChange('linkingStatus', 'all')}
                  className="removeTag"
                  disabled={isReadOnly}
                >
                  ×
                </button>
              </span>
            )}
            {(localFilters.mappingStatus || 'all') !== 'all' && (
              <span className="filterTag">
                Mapping: {localFilters.mappingStatus === 'mapped' ? 'Mapped' : 'Unmapped'}
                <button
                  onClick={() => handleInputChange('mappingStatus', 'all')}
                  className="removeTag"
                  disabled={isReadOnly}
                >
                  ×
                </button>
              </span>
            )}
            {(localFilters.inventory?.min || localFilters.inventory?.max) && (
              <span className="filterTag">
                Inventory: {localFilters.inventory?.min || '0'}-{localFilters.inventory?.max || '∞'}
                <button 
                  onClick={() => handleInputChange('inventory', { min: '', max: '' })}
                  className="removeTag"
                  disabled={isReadOnly}
                >
                  ×
                </button>
              </span>
            )}
            {localFilters.dateRange !== 'all' && (
              <span className="filterTag">
                Date: {localFilters.dateRange}
                <button 
                  onClick={() => handleInputChange('dateRange', 'all')}
                  className="removeTag"
                  disabled={isReadOnly}
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductFilters;
